import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Dialog } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { extractError } from '@/api/client';
import type { Employee } from '@/types';
import {
  documentsRhApi, ESSAI_AFFICHE, LIBELLE_CATEGORIE, type CategorieContrat, type NatureContrat,
} from '@/api/documentsRh';

/**
 * Lot CONTRATS (20/10/2026) — formulaire du contrat de travail. Textes validés par le cabinet ; le serveur refuse
 * d'émettre tant qu'un champ obligatoire manque (un contrat signé ne contient jamais de blanc).
 */

const Field = ({ label, children, aide }: { label: string; children: ReactNode; aide?: string }) => (
  <label className="block space-y-1">
    <span className="text-xs font-medium text-gray-700">{label}</span>
    {children}
    {aide && <span className="block text-[11px] text-gray-500">{aide}</span>}
  </label>
);
const SELECT = 'flex h-10 w-full rounded-md border border-gray-200 bg-white px-3 text-sm';
const AREA = 'w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm';

type Form = Record<string, string> & { nature: NatureContrat; categorie: CategorieContrat | '' };
const VIDE: Form = {
  nature: 'cdi', categorie: '', dateDebut: '', dateFin: '', nationalite: '', pieceIdentite: '', adresse: '', telephone: '',
  missions: '', responsable: '', lieuTravail: '', zoneMobilite: '', delaiMobiliteJours: '', heuresHebdo: '', salaireBaseFcfa: '',
  primeTransportFcfa: '', jourPaiement: '', villeLitiges: '', motifCdd: '', motifDetail: '', encadrement: '', limiteDepenseFcfa: '',
  remiseMaxPct: '', avantages: '', primeObjectifs: '', nonSollicitationMois: '', ncActivite: '', ncZone: '', ncDureeMois: '',
  ncIndemnite: '', nonConcurrence: '', etablissement: '', filiere: '', typeStage: '', tuteur: '', enseignantReferent: '', gratificationFcfa: '',
};
const nombre = (v: string) => (v.trim() === '' ? undefined : Number(v.replace(/\s/g, '').replace(',', '.')));
const texte = (v: string) => (v.trim() === '' ? undefined : v.trim());

export const ContratDialog = ({ open, onClose, employee, onEmis }: {
  open: boolean; onClose: () => void; employee: Employee; onEmis: (d: { id: string; numero: string }) => void;
}) => {
  const [f, setF] = useState<Form>(VIDE);
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const pre = useQuery({ queryKey: ['hr', 'contrat', 'preremplir', employee.id], queryFn: () => documentsRhApi.preremplirContrat(employee.id), enabled: open });

  useEffect(() => {
    if (!open || !pre.data) return;
    const p = pre.data;
    setF({
      ...VIDE, nature: p.nature, dateDebut: p.dateDebut, dateFin: p.dateFin ?? '', telephone: p.telephone ?? '',
      salaireBaseFcfa: p.salaireBaseFcfa != null ? String(p.salaireBaseFcfa) : '', primeTransportFcfa: p.primeTransportFcfa != null ? String(p.primeTransportFcfa) : '',
      heuresHebdo: String(p.heuresHebdo), villeLitiges: p.villeLitiges ?? '', lieuTravail: p.lieuTravail ?? '',
    });
    setErreur(null);
  }, [open, pre.data]);

  const set = (k: keyof Form, v: string) => setF((x) => ({ ...x, [k]: v }));
  const stage = f.nature === 'stage';
  const cadreCdi = f.nature === 'cdi' && f.categorie === 'cadre';
  const encadrant = !stage && (f.categorie === 'agent_maitrise' || f.categorie === 'technicien');

  const envoyer = async (e: FormEvent) => {
    e.preventDefault();
    setErreur(null); setEnvoi(true);
    try {
      const d = await documentsRhApi.emettreContrat({
        employeeId: employee.id, nature: f.nature, categorie: stage ? undefined : f.categorie || undefined,
        dateDebut: f.dateDebut, dateFin: texte(f.dateFin), nationalite: f.nationalite, pieceIdentite: f.pieceIdentite, adresse: f.adresse,
        telephone: texte(f.telephone), missions: f.missions, responsable: texte(f.responsable), lieuTravail: f.lieuTravail,
        zoneMobilite: texte(f.zoneMobilite), delaiMobiliteJours: nombre(f.delaiMobiliteJours), heuresHebdo: nombre(f.heuresHebdo),
        salaireBaseFcfa: nombre(f.salaireBaseFcfa), primeTransportFcfa: nombre(f.primeTransportFcfa), jourPaiement: nombre(f.jourPaiement),
        villeLitiges: f.villeLitiges, motifCdd: texte(f.motifCdd), motifDetail: texte(f.motifDetail), encadrement: texte(f.encadrement),
        limiteDepenseFcfa: nombre(f.limiteDepenseFcfa), remiseMaxPct: nombre(f.remiseMaxPct), avantages: texte(f.avantages),
        primeObjectifs: texte(f.primeObjectifs), nonSollicitationMois: nombre(f.nonSollicitationMois),
        nonConcurrence: cadreCdi && f.nonConcurrence === 'oui'
          ? { activite: f.ncActivite, zone: f.ncZone, dureeMois: nombre(f.ncDureeMois), indemnite: f.ncIndemnite } : undefined,
        etablissement: texte(f.etablissement), filiere: texte(f.filiere), typeStage: texte(f.typeStage), tuteur: texte(f.tuteur),
        enseignantReferent: texte(f.enseignantReferent), gratificationFcfa: nombre(f.gratificationFcfa),
      });
      onEmis(d);
      onClose();
    } catch (err) {
      setErreur(extractError(err).message);
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} title="Contrat de travail" subtitle={`${employee.fullName} — ${employee.matricule}`} size="lg">
      <form onSubmit={envoyer} className="space-y-3">
        {erreur && <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{erreur}</div>}
        <p className="text-xs text-gray-500">
          Texte validé par le cabinet juridique. Le nom, la naissance, le poste et la catégorie viennent de la fiche du salarié :
          corrigez la fiche avant d’émettre si besoin. Un contrat émis ne se modifie plus (il s’annule avec un motif).
        </p>

        <div className="grid grid-cols-3 gap-3">
          <Field label="Nature *">
            <select className={SELECT} value={f.nature} onChange={(e) => set('nature', e.target.value)}>
              <option value="cdi">CDI</option><option value="cdd">CDD</option><option value="stage">Convention de stage</option>
            </select>
          </Field>
          {!stage && (
            <Field label="Catégorie *">
              <select className={SELECT} value={f.categorie} onChange={(e) => set('categorie', e.target.value)}>
                <option value="">—</option>
                {(Object.keys(LIBELLE_CATEGORIE) as CategorieContrat[]).map((c) => <option key={c} value={c}>{LIBELLE_CATEGORIE[c]}</option>)}
              </select>
            </Field>
          )}
          <Field label="Période d’essai">
            <div className="flex h-10 items-center text-sm font-semibold">
              {ESSAI_AFFICHE(f.nature, f.categorie, f.dateDebut, f.dateFin)} — non renouvelable{f.nature === 'cdi' && f.categorie ? ' ; préavis identique' : ''}
            </div>
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Date de début *"><Input type="date" value={f.dateDebut} onChange={(e) => set('dateDebut', e.target.value)} required /></Field>
          {f.nature !== 'cdi' && <Field label="Date de fin *"><Input type="date" value={f.dateFin} min={f.dateDebut} onChange={(e) => set('dateFin', e.target.value)} /></Field>}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Nationalité *"><Input value={f.nationalite} onChange={(e) => set('nationalite', e.target.value)} placeholder="ivoirienne" /></Field>
          <Field label="Pièce d’identité *"><Input value={f.pieceIdentite} onChange={(e) => set('pieceIdentite', e.target.value)} placeholder="CNI n° …" /></Field>
          <Field label="Adresse *"><Input value={f.adresse} onChange={(e) => set('adresse', e.target.value)} /></Field>
          <Field label="Téléphone"><Input value={f.telephone} onChange={(e) => set('telephone', e.target.value)} /></Field>
        </div>

        <Field label={stage ? 'Programme du stage *' : 'Missions principales du poste *'}>
          <textarea className={AREA} rows={2} value={f.missions} onChange={(e) => set('missions', e.target.value)} />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          {!stage && <Field label="Responsable hiérarchique *"><Input value={f.responsable} onChange={(e) => set('responsable', e.target.value)} placeholder="le Directeur de production" /></Field>}
          <Field label="Lieu de travail *"><Input value={f.lieuTravail} onChange={(e) => set('lieuTravail', e.target.value)} /></Field>
          <Field label="Inspection du travail de (ville) *"><Input value={f.villeLitiges} onChange={(e) => set('villeLitiges', e.target.value)} /></Field>
        </div>

        {!stage && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Zone de mobilité *" aide="Complète la phrase « sur tout site ou chantier de la Société situé … »">
                <Input value={f.zoneMobilite} onChange={(e) => set('zoneMobilite', e.target.value)} placeholder="en Côte d’Ivoire" />
              </Field>
              <Field label="Délai de prévenance de mutation (jours) *"><Input inputMode="numeric" value={f.delaiMobiliteJours} onChange={(e) => set('delaiMobiliteJours', e.target.value)} /></Field>
            </div>
            <div className="grid grid-cols-4 gap-3">
              <Field label="Heures / semaine *"><Input inputMode="decimal" value={f.heuresHebdo} onChange={(e) => set('heuresHebdo', e.target.value)} /></Field>
              <Field label="Salaire de base (F) *"><Input inputMode="numeric" value={f.salaireBaseFcfa} onChange={(e) => set('salaireBaseFcfa', e.target.value)} /></Field>
              <Field label="Prime de transport (F) *"><Input inputMode="numeric" value={f.primeTransportFcfa} onChange={(e) => set('primeTransportFcfa', e.target.value)} /></Field>
              <Field label="Payé au plus tard le *"><Input inputMode="numeric" value={f.jourPaiement} onChange={(e) => set('jourPaiement', e.target.value)} placeholder="5" /></Field>
            </div>
          </>
        )}

        {f.nature === 'cdd' && (
          <div className="grid grid-cols-2 gap-3">
            <Field label="Motif du CDD *">
              <select className={SELECT} value={f.motifCdd} onChange={(e) => set('motifCdd', e.target.value)}>
                <option value="">—</option><option value="remplacement">Remplacement d’un salarié absent</option>
                <option value="surcroit">Surcroît temporaire d’activité</option><option value="chantier">Chantier précis</option>
              </select>
            </Field>
            <Field label={f.motifCdd === 'remplacement' ? 'Personne remplacée (nom, emploi) *' : 'Précision (commande, chantier, période) *'}>
              <Input value={f.motifDetail} onChange={(e) => set('motifDetail', e.target.value)} />
            </Field>
          </div>
        )}

        {encadrant && (
          <Field label="Personnes encadrées" aide="Facultatif, par exemple « l’équipe de coulage (6 personnes) »">
            <Input value={f.encadrement} onChange={(e) => set('encadrement', e.target.value)} />
          </Field>
        )}

        {cadreCdi && (
          <div className="space-y-3 rounded-md border border-gray-200 p-3">
            <div className="text-xs font-semibold">Cadre — pouvoirs et protection</div>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Dépense max. sans co-signature (F) *"><Input inputMode="numeric" value={f.limiteDepenseFcfa} onChange={(e) => set('limiteDepenseFcfa', e.target.value)} /></Field>
              <Field label="Remise commerciale max. (%) *"><Input inputMode="decimal" value={f.remiseMaxPct} onChange={(e) => set('remiseMaxPct', e.target.value)} /></Field>
              <Field label="Non-sollicitation (mois) *"><Input inputMode="numeric" value={f.nonSollicitationMois} onChange={(e) => set('nonSollicitationMois', e.target.value)} /></Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Avantages"><Input value={f.avantages} onChange={(e) => set('avantages', e.target.value)} placeholder="véhicule, téléphone…" /></Field>
              <Field label="Prime sur objectifs"><Input value={f.primeObjectifs} onChange={(e) => set('primeObjectifs', e.target.value)} placeholder="jusqu’à … par an" /></Field>
            </div>
            <Field label="Clause de non-concurrence">
              <select className={SELECT} value={f.nonConcurrence} onChange={(e) => set('nonConcurrence', e.target.value)}>
                <option value="">Non</option><option value="oui">Oui</option>
              </select>
            </Field>
            {f.nonConcurrence === 'oui' && (
              <div className="grid grid-cols-2 gap-3">
                <Field label="Activité visée *"><Input value={f.ncActivite} onChange={(e) => set('ncActivite', e.target.value)} placeholder="la préfabrication béton" /></Field>
                <Field label="Zone *"><Input value={f.ncZone} onChange={(e) => set('ncZone', e.target.value)} placeholder="la région du Gbêkê" /></Field>
                <Field label="Durée (mois) *"><Input inputMode="numeric" value={f.ncDureeMois} onChange={(e) => set('ncDureeMois', e.target.value)} /></Field>
                <Field label="Indemnité versée *" aide="Telle qu’elle sera imprimée, par exemple « 50 % de son dernier salaire de base, chaque mois »">
                  <Input value={f.ncIndemnite} onChange={(e) => set('ncIndemnite', e.target.value)} />
                </Field>
              </div>
            )}
          </div>
        )}

        {stage && (
          <div className="grid grid-cols-2 gap-3">
            <Field label="Établissement *"><Input value={f.etablissement} onChange={(e) => set('etablissement', e.target.value)} /></Field>
            <Field label="Filière et niveau *"><Input value={f.filiere} onChange={(e) => set('filiere', e.target.value)} /></Field>
            <Field label="Type de stage *"><Input value={f.typeStage} onChange={(e) => set('typeStage', e.target.value)} placeholder="fin d’études" /></Field>
            <Field label="Gratification mensuelle (F) *"><Input inputMode="numeric" value={f.gratificationFcfa} onChange={(e) => set('gratificationFcfa', e.target.value)} /></Field>
            <Field label="Tuteur dans l’entreprise *"><Input value={f.tuteur} onChange={(e) => set('tuteur', e.target.value)} /></Field>
            <Field label="Enseignant référent *"><Input value={f.enseignantReferent} onChange={(e) => set('enseignantReferent', e.target.value)} /></Field>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>Fermer</Button>
          <Button type="submit" disabled={envoi || pre.isLoading}>{envoi ? 'Émission…' : 'Émettre le contrat'}</Button>
        </div>
      </form>
    </Dialog>
  );
};
